<template>
  <div class="publishing-upload-list">
    <div class="header">
      <h1>{{ $t('PUBLISHING.UPLOAD.HEADING') }}</h1>
      <p>{{ $t('PUBLISHING.UPLOAD.SUBHEADING') }}</p>
    </div>
    <div class="upload-content">
      <p v-if="fileLimitExceeded" class="parallel-limit-hint">
        {{ $t('DESIGNS.UPLOAD.MAXIMUM_NUMBER_EXCEEDED', { count: fileLimit }) }}
      </p>
      <transition-group name="fade">
        <PublishingUploadItem
          v-for="uploadItem in unsuccessfullUploadItems"
          :key="uploadItem.id"
          :file="uploadItem.file"
          class="item"
          @on-done="() => onUploadItemDone(uploadItem.id)"
        />
      </transition-group>
      <transition name="fade">
        <h4 v-if="allSuccessfull" class="success-msg">
          <Icon icon="accept" />
          {{ $t('PUBLISHING.UPLOAD.SUCCESS') }}
        </h4>
      </transition>
    </div>
  </div>
</template>

<script>
import { delay } from 'commons/utils';
import PublishingUploadItem from './PublishingUploadItem.vue';
import designUploadService from 'commons/api/designUploadService/designUploadServiceV2';
import analytics from 'commons/tracking/analytics';

export default {
  name: 'PublishingUploadList',
  components: { PublishingUploadItem },
  props: {
    files: { type: Array, required: true },
  },
  emits: ['onDone'],
  data() {
    return {
      uploadItems: [],
      fileLimit: designUploadService.getFileLimit(),
      fileLimitExceeded: false,
      allSuccessfull: false,
    };
  },
  computed: {
    unsuccessfullUploadItems() {
      return this.uploadItems.filter((uploadItem) => !uploadItem.success);
    },
    allUploadItemsDone() {
      return this.uploadItems.every(
        (uploadItem) => uploadItem.success || uploadItem.error,
      );
    },
  },
  watch: {
    allUploadItemsDone: {
      deep: true,
      async handler(val) {
        if (val) {
          const successCount = this.uploadItems.filter(
            (uploadItem) => uploadItem.success,
          )?.length;
          const errorCount = this.uploadItems.filter(
            (uploadItem) => uploadItem.error,
          )?.length;
          await delay(1000);
          if (!errorCount) {
            this.allSuccessfull = true;
          }
          await delay(2000);
          this.$emit('onDone', { successCount, errorCount });

          analytics.logEvent('composition_upload_finished', {
            success: successCount,
            failed: errorCount,
          });
        }
      },
    },
  },
  async created() {
    if (this.files.length > this.fileLimit) {
      this.fileLimitExceeded = true;
    }

    this.files.slice(0, this.fileLimit).forEach((file, index) => {
      this.uploadItems.push({
        id: index,
        file,
      });
    });

    analytics.logEvent('composition_upload', {
      value: this.files.length,
      files: this.files.length,
    });
  },
  methods: {
    onUploadItemDone(uploadItemId) {
      const uploadItem = this.uploadItems.find(
        (item) => item.id === uploadItemId,
      );
      uploadItem.success = true;
    },
    onUploadItemFailed(uploadItem) {
      uploadItem.error = true;
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/scss/styleguide/colors';

.publishing-upload-list {
  .header {
    text-align: center;
    margin: 0 0 16px 0;

    h1 {
      margin: 0 0 16px 0;
    }

    p {
      margin: 0;
      color: $grey60;
    }
  }

  .item {
    &:not(:last-child) {
      margin: 0 0 16px 0;
    }
  }

  .parallel-limit-hint {
    font-style: italic;
    color: $grey60;
  }

  .upload-content {
    min-height: 48px;
  }

  .success-msg {
    color: $pa-color-green;
    font-weight: bold;
    margin: 0;
    padding: 8px 0;
    display: flex;
    align-items: center;
    display: block;
    text-align: center;

    .icon {
      margin-right: 8px;
    }
  }
}
</style>
